import { useMutation, useQuery, useQueryClient } from 'react-query';
import { couponEquipmentServices } from './company.services';
import notificationError from '@/utils/functions/notificationError';
import { notification } from 'antd';
import { AxiosError } from 'axios';

const COUPON_LIST_QUERY = 'coupon_list';
const COUPON_COMPANY_CONFIG_QUERY = 'coupon_company_config';

export function useCouponEquipamentList(companyId?: number) {
  return useQuery(
    [COUPON_LIST_QUERY],
    () => couponEquipmentServices.list(companyId),
    {
      enabled: !!companyId,
      onError(error) {
        notificationError(error);
      },
    },
  );
}

export function useCouponEquipmentCreate() {
  const queryClient = useQueryClient();

  return useMutation(couponEquipmentServices.create, {
    async onSuccess() {
      notification.success({
        message: 'Equipamento SAT cadastrado',
        description: 'O equipamento SAT foi cadastrado com sucesso',
      });
      await queryClient.invalidateQueries([COUPON_LIST_QUERY]);
    },

    onError(error) {
      notificationError(error);
    },
  });
}

export function useCouponEquipmentEdit() {
  const queryClient = useQueryClient();

  return useMutation(couponEquipmentServices.edit, {
    async onSuccess() {
      notification.success({
        message: 'Equipamento SAT editado',
        description: 'O equipamento SAT foi editado com sucesso',
      });
      await queryClient.invalidateQueries([COUPON_LIST_QUERY]);
    },

    onError(error) {
      notificationError(error);
    },
  });
}

export function useCouponEquipamentRemove() {
  const queryClient = useQueryClient();

  return useMutation(couponEquipmentServices.remove, {
    async onSuccess() {
      notification.success({
        message: 'Equipamento SAT removido',
        description: 'O equipamento SAT foi removido com sucesso',
      });
      await queryClient.invalidateQueries([COUPON_LIST_QUERY]);
    },

    onError(error) {
      notificationError(error);
    },
  });
}

export function useCouponCompanyConfigDetail(
  companyId?: number,
  options?: { refetchOnFocus?: boolean },
) {
  return useQuery(
    [COUPON_COMPANY_CONFIG_QUERY, { companyId }],
    () => couponEquipmentServices.configDetail(companyId),
    {
      enabled: !!companyId,
      refetchOnWindowFocus: !!options?.refetchOnFocus,
      onError(error: AxiosError) {
        if (error?.response?.status !== 404) {
          notificationError(error);
        }
      },
    },
  );
}

export function useCouponCompanyConfigCreate() {
  const queryClient = useQueryClient();

  return useMutation(couponEquipmentServices.configCreate, {
    async onSuccess(_, data) {
      notification.success({
        message: 'Dados do cupom salvo',
        description:
          'As configuraçãoes padrões do cupom fiscal foram salvas com sucesso!',
      });
      await queryClient.invalidateQueries([
        COUPON_COMPANY_CONFIG_QUERY,
        { companyId: data.companyId },
      ]);
    },

    onError(error) {
      notificationError(error, { message: 'Configuração do cupom' });
    },
  });
}

export function useCouponCompanyConfigEnable() {
  const queryClient = useQueryClient();

  return useMutation(couponEquipmentServices.configEnable, {
    async onSuccess(_, companyId) {
      notification.success({
        message: 'Cupom fiscal ativado com sucesso!',
      });
      await queryClient.invalidateQueries([
        COUPON_COMPANY_CONFIG_QUERY,
        { companyId },
      ]);
    },

    onError(error) {
      notificationError(error, { message: 'Erro de ativação' });
    },
  });
}

export function useCouponCompanyConfigDisable() {
  const queryClient = useQueryClient();

  return useMutation(couponEquipmentServices.configDisable, {
    async onSuccess(_, companyId) {
      notification.success({
        message: 'Cupom fiscal desativado com sucesso!',
      });
      await queryClient.invalidateQueries([
        COUPON_COMPANY_CONFIG_QUERY,
        { companyId },
      ]);
    },

    onError(error) {
      notificationError(error, { message: 'Erro de desativação' });
    },
  });
}

export function useCouponCompanyConfigPart() {
  const queryClient = useQueryClient();

  return useMutation(couponEquipmentServices.configPart, {
    async onSuccess(_, companyId) {
      notification.success({
        message: 'CFE alterado com sucesso!',
      });
      await queryClient.invalidateQueries([
        COUPON_COMPANY_CONFIG_QUERY,
        { companyId },
      ]);
    },

    onError(error) {
      notificationError(error, { message: 'Erro ao atualizar CFE' });
    },
  });
}

export function useCouponCompanyConfigTask() {
  const queryClient = useQueryClient();

  return useMutation(couponEquipmentServices.configTask, {
    async onSuccess(_, companyId) {
      notification.success({
        message: 'CFS alterado com sucesso!',
      });
      await queryClient.invalidateQueries([
        COUPON_COMPANY_CONFIG_QUERY,
        { companyId },
      ]);
    },

    onError(error) {
      notificationError(error, { message: 'Erro ao atualizar CFS' });
    },
  });
}
